/**
 * Flight result filtering and the tab selections of FlightContainerWidget.
 *
 * Two independent steps, applied in this order:
 *   1. `filterFlights`  — the sidebar's stops / price / departure-time filters.
 *   2. `selectForTab`   — what each tab shows out of what survived step 1.
 * Filtering first means a tab's "30%" is 30% of the flights the traveller
 * still has in play, not 30% of flights they've already ruled out.
 */
import type { FlightCardPayload } from '../components/FlightCardWidget';
import type { FlightFilters } from '../types/filterSidebar';
import { durationMinutes, priceAmount } from '../components/flightPresentation';

export type FlightTab = 'All' | 'Best Value' | 'Cheapest' | 'Fastest';

/** Share of the returned flights that the Cheapest and Fastest tabs keep. */
export const TAB_SHARE = 0.3;
/** Best Value keeps flights up to this much longer than the shortest one. */
export const BEST_VALUE_DURATION_TOLERANCE = 0.3;

/**
 * Departure-time buckets, on the local clock of the departure airport (the
 * timestamp's own wall time, never converted to the viewer's zone):
 *
 *   Morning    05:00 – 11:59
 *   Afternoon  12:00 – 17:59
 *   Evening    18:00 – 23:59
 *
 * Departures from 00:00 to 04:59 (red-eyes) fall in none of the three, so
 * they are shown under "Any" only rather than mislabelled as morning or
 * evening.
 */
export type DepartureBucket = 'morning' | 'afternoon' | 'evening';

export const departureBucket = (hour: number): DepartureBucket | null => {
  if (hour >= 5 && hour < 12) return 'morning';
  if (hour >= 12 && hour < 18) return 'afternoon';
  if (hour >= 18 && hour < 24) return 'evening';
  return null;
};

const offerDuration = (offer: FlightCardPayload): number =>
  durationMinutes(offer.itineraries?.[0]?.duration || offer.duration);

const offerPrice = (offer: FlightCardPayload): number => priceAmount(offer.price?.total);

/** Stops on the leg shown on the card; falls back to counting segments. */
const offerStops = (offer: FlightCardPayload): number => {
  if (typeof offer.stops === 'number') return offer.stops;
  const segments = offer.itineraries?.[0]?.segments;
  return segments ? Math.max(0, segments.length - 1) : 0;
};

/** Local departure hour of the leg, read straight from the timestamp. */
const offerDepartureHour = (offer: FlightCardPayload): number | null => {
  const at = offer.itineraries?.[0]?.segments?.[0]?.departure?.at;
  const m = at?.match(/T(\d{2}):\d{2}/);
  return m ? Number(m[1]) : null;
};

const matchesStops = (offer: FlightCardPayload, stops: FlightFilters['stops']): boolean => {
  if (stops === 'any') return true;
  const n = offerStops(offer);
  if (stops === 'direct') return n === 0;
  if (stops === '1stop') return n === 1;
  return n >= 2; // '2stop' — shown as "2+ stops"
};

const matchesTime = (offer: FlightCardPayload, time: FlightFilters['time']): boolean => {
  if (time === 'any') return true;
  const hour = offerDepartureHour(offer);
  return hour !== null && departureBucket(hour) === time;
};

const matchesPrice = (offer: FlightCardPayload, maxPrice: number | null): boolean =>
  !maxPrice || offerPrice(offer) <= maxPrice;

export const filterFlights = (
  offers: FlightCardPayload[],
  filters?: FlightFilters,
): FlightCardPayload[] => {
  if (!filters) return offers;
  return offers.filter(
    (offer) =>
      matchesStops(offer, filters.stops) &&
      matchesPrice(offer, filters.maxPrice) &&
      matchesTime(offer, filters.time),
  );
};

const portion = (count: number): number => Math.max(1, Math.ceil(count * TAB_SHARE));

/**
 * What a tab shows out of `offers`:
 *   All         every flight, in the order the assistant ranked them.
 *   Cheapest    the cheapest 30%, cheapest first.
 *   Fastest     the shortest 30%, shortest first.
 *   Best Value  every flight within 30% of the shortest flight's duration,
 *               cheapest first.
 * At least one flight is kept whenever there is one.
 */
export const selectForTab = (offers: FlightCardPayload[], tab: FlightTab): FlightCardPayload[] => {
  if (tab === 'All' || offers.length === 0) return offers;

  if (tab === 'Cheapest') {
    return [...offers].sort((a, b) => offerPrice(a) - offerPrice(b)).slice(0, portion(offers.length));
  }

  if (tab === 'Fastest') {
    return [...offers].sort((a, b) => offerDuration(a) - offerDuration(b)).slice(0, portion(offers.length));
  }

  // Best Value — offers with no readable duration can't be compared, so
  // they're left out rather than guessed at.
  const shortest = Math.min(...offers.map(offerDuration));
  if (!Number.isFinite(shortest)) return [];
  const limit = shortest * (1 + BEST_VALUE_DURATION_TOLERANCE);
  return offers
    .filter((offer) => offerDuration(offer) <= limit)
    .sort((a, b) => offerPrice(a) - offerPrice(b));
};
